import React, { useEffect, useState, useMemo } from "react";
import { Modal, Row, Col, notification, Button, Skeleton, Divider } from "antd";
import axios from "axios";

const confirm = Modal.confirm;

const openNotificationWithIcon = (type, message, description) => {
    notification[type]({
        message: message,
        description: description
    });
};

const parseJson = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "object" && !Array.isArray(value)) return value;
    try {
        return JSON.parse(value) || fallback;
    } catch {
        return fallback;
    }
};

// ── Constantes ──────────────────────────────────────────────────────────────

const AREAS_CARACTERISTICAS = [
    { key: "vida_diaria", label: "Vida diária: autonomia/independência" },
    { key: "comunicacao_linguagem", label: "Comunicação/Linguagem" },
    { key: "motricidade_fina", label: "Motricidade fina" },
    { key: "motricidade_grossa", label: "Motricidade grossa" },
    { key: "habilidades_cognitivas", label: "Habilidades cognitivas: atenção, concentração, compreensão" },
    { key: "habilidades_interpessoais", label: "Habilidades interpessoais/afetivas" },
    { key: "habilidades_academicas", label: "Habilidades acadêmicas: leitura, escrita, cálculos matemáticos" }
];

const ITENS_AVALIACAO = [
    { secao: "LINGUAGEM - COMUNICAÇÃO" },
    { key: "expressa_clareza", label: "Se expressa com clareza?" },
    { key: "vocabulo_amplo", label: "Possui vocábulo amplo?" },
    { key: "transmite_recados", label: "Transmite recados?" },
    { key: "narra_experiencias", label: "Narra pequenas experiências pessoais?" },

    { secao: "LINGUAGEM ESCRITA" },
    { key: "escreve_nome", label: "Escreve seu nome?" },
    { key: "representa_letras", label: "Consegue representar graficamente as letras do alfabeto?" },
    { key: "escreve_palavras", label: "Escreve palavras?" },

    { secao: "LEITURA" },
    { key: "identifica_letras_nome", label: "Identifica todas as letras do seu nome?" },
    { key: "reconhece_alfabeto", label: "Reconhece todas as letras do alfabeto?" },
    { key: "identifica_inicial_final", label: "Identifica letra inicial e final de palavras?" },
    { key: "associa_palavras_imagem", label: "Associa palavras à imagem?" },
    { key: "le_palavras_simples", label: "Lê palavras simples?" },
    { key: "le_palavras_complexas", label: "Lê palavras complexas?" },
    { key: "le_textos", label: "Faz leitura de texto(s)?" },

    { secao: "ATENÇÃO E CONCENTRAÇÃO" },
    { key: "demora_tarefas", label: "Detém muito tempo na execução das tarefas?" },
    { key: "disperso", label: "É disperso e muda o foco da atividade para outra?" },
    { key: "agitado", label: "É agitado, não concluindo as atividades propostas?" },

    { secao: "PSICOMOTRICIDADE" },
    { key: "identifica_direita_esquerda", label: "Identifica direita e esquerda?" },
    { key: "nocao_espaco", label: "Tem noção de espaço (longe/perto, dentro/fora)?" },
    { key: "nocao_grandeza", label: "Tem noção de grandeza (maior/menor)?" },
    { key: "nocao_posicao", label: "Tem noção de posição (na frente/meio/atrás)?" },

    { secao: "COORDENAÇÃO VISOMOTORA" },
    { key: "coordenacao_motora", label: "Atira? Pega? Solta? Preensão palmar? Pinça? Rasga? Dobra? Modela?" },
    { key: "recorta_reta", label: "Recorta em linha reta?" },
    { key: "recorta_curva", label: "Recorta linha curva?" },
    { key: "pega_instrumentos", label: "Pega corretamente a caneta, tesoura, lápis, borracha?" },
    { key: "amarra_no", label: "Amarra nó?" },

    { secao: "MEMÓRIA VISUAL" },
    { key: "interpreta_gravuras", label: "Interpreta gravuras?" },
    { key: "memoriza_imagens", label: "Memoriza imagens?" },
    { key: "discrimina_objetos", label: "Discrimina objetos?" },
    { key: "monta_puzzles", label: "Monta quebra-cabeças e jogos de encaixe?" },
    { key: "identifica_figuras_iguais", label: "Identifica figuras iguais?" },

    { secao: "MEMÓRIA AUDITIVA" },
    { key: "identifica_sons", label: "Identifica os vários tipos de sons?" },
    { key: "percebe_estimulos", label: "Percebe estímulos sonoros?" },
    { key: "reproduz_sons", label: "Reproduz sons?" },

    { secao: "MATEMÁTICA – RACIOCÍNIO LÓGICO" },
    { key: "quantidade_numeral", label: "Relaciona a quantidade com o numeral?" },
    { key: "conta_oralmente", label: "Conta oralmente?" },
    { key: "realiza_adicao", label: "Realiza adição?" },
    { key: "realiza_subtracao", label: "Realiza subtração?" },
    { key: "figuras_geometricas", label: "Identifica figuras geométricas?" },
    { key: "cores_primarias", label: "Reconhece as cores primárias?" },
    { key: "diferencia_numeros", label: "Sabe diferenciar os números de outros símbolos?" },
    { key: "escreve_numeros", label: "Escreve os números?" },

    { secao: "ESTRUTURAÇÃO ESPAÇO/TEMPO" },
    { key: "nocao_espaco_geral", label: "Demonstra noção de espaço (sala de aula, casa, escola, etc.)?" },
    { key: "nocao_temporalidade", label: "Apresenta noções de temporalidade?" },
    { key: "organiza_historia", label: "Demonstra capacidade de organização de história em sequência através de gravuras?" },

    { secao: "MOVIMENTO E ARTES" },
    { key: "coord_motora_grossa", label: "Apresenta coordenação motora grossa?" },
    { key: "desenhos_detalhes", label: "Faz desenhos com riqueza de detalhes?" },
    { key: "pinta_linhas", label: "Pinta desenhos obedecendo as linhas?" },
    { key: "recorte_linhas", label: "Faz recorte obedecendo as linhas?" },
    { key: "modela_objetos", label: "Dá forma a objetos, animais e outros seres com massa de modelar?" }
];

const TERAPIAS = ["Equoterapia", "Fisioterapia", "Fonoaudiologia", "Psicologia", "Terapia ocupacional", "Outros"];

const BADGE_COLORS = {
    ALC: { background: "#d9ead3", color: "#274e13" },
    EPR: { background: "#fff2cc", color: "#7f6000" },
    NAL: { background: "#fce5cd", color: "#7f3f00" },
    NAV: { background: "#f3f3f3", color: "#666" }
};

const TIPOS_INTERVENCAO = [
    { key: "familiar", label: "Familiar" },
    { key: "arquitetonico", label: "Arquitetônico/mobiliário" },
    { key: "atitudinal", label: "Atitudinal" },
    { key: "pedagogico", label: "Pedagógico" },
    { key: "outro", label: "Outro" }
];

// ── Componente Badge ────────────────────────────────────────────────────────

const BadgeAvaliacao = ({ valor }) => {
    if (!valor) return <span style={{ color: "#999", fontSize: 11 }}>—</span>;
    const style = BADGE_COLORS[valor] || {};
    return <span style={{ ...style, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: "bold" }}>{valor}</span>;
};

// ── Modal ───────────────────────────────────────────────────────────────────

const ModalPlanoAtendimentoPEI = ({ id, visible, onCancel, onDelete }) => {
    const [plano, setPlano] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const apresentacao = useMemo(() => parseJson(plano?.apresentacao), [plano?.apresentacao]);
    const atendimentos = useMemo(() => parseJson(plano?.atendimentos_clinicos), [plano?.atendimentos_clinicos]);
    const caracteristicas = useMemo(() => parseJson(plano?.caracteristicas_aluno), [plano?.caracteristicas_aluno]);
    const intervencoes = useMemo(() => parseJson(plano?.intervencoes), [plano?.intervencoes]);
    const avaliacao = useMemo(() => parseJson(plano?.avaliacao), [plano?.avaliacao]);

    const carregarDetalhe = id => {
        setIsLoading(true);
        axios({ method: "get", url: "/api/plano-atendimento/carregar-plano-atendimento-pei", params: { id } })
            .then(response => setPlano(response.data))
            .catch(error => {
                openNotificationWithIcon("error", "Erro", error.response?.data || "Erro ao carregar o PEI.");
            })
            .finally(() => setIsLoading(false));
    };

    const validarExcluir = () => {
        confirm({
            title: "Pretende remover este plano de ensino individualizado?",
            okText: "Confirmar",
            cancelText: "Cancelar",
            onOk: () => excluir()
        });
    };

    const excluir = () => {
        axios({ method: "put", url: "/api/plano-atendimento/excluir-plano-atendimento", params: { id } })
            .then(() => {
                openNotificationWithIcon("success", "Removido", "O plano de ensino individualizado foi removido com sucesso.");
                onDelete();
            })
            .catch(error => {
                openNotificationWithIcon("error", "Erro", error.response?.data || "Erro ao excluir o plano de ensino individualizado.");
            });
    };

    useEffect(() => {
        if (id && visible) carregarDetalhe(id);
    }, [id]);

    // ── Render helpers ───────────────────────────────────────────────────────

    const renderField = (label, value, span = 12) => (
        <Col span={span} style={{ marginBottom: 12 }}>
            <span className="field-label">{label}</span>
            <Skeleton loading={isLoading} paragraph={false} active className="field-skeleton">
                <p className="field-value" style={{ fontWeight: "bold", marginBottom: 0 }}>
                    {value || "-"}
                </p>
            </Skeleton>
        </Col>
    );

    const renderFieldWide = (label, value) => (
        <Col span={24} style={{ marginBottom: 12 }}>
            {label && <span className="field-label">{label}</span>}
            <Skeleton loading={isLoading} title={false} active className="field-skeleton">
                <p className="field-value--wide" style={{ fontWeight: "bold", marginBottom: 0 }}>
                    {value || "-"}
                </p>
            </Skeleton>
        </Col>
    );

    return (
        <Modal
            title={
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 16 }}>Plano de Ensino Individualizado (PEI) AEE</div>
                    <div style={{ fontSize: 12, color: "#888" }}>Visualização do plano de ensino individualizado</div>
                </div>
            }
            className="modal-detalhe-info"
            visible={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancelar" onClick={onCancel}>
                    Fechar
                </Button>,
                <Button key="eliminar" className="modal-detalhe-info-delete-btn" type="danger" onClick={validarExcluir}>
                    Excluir PEI
                </Button>
            ]}
            centered
            width={900}
            bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
        >
            <div className="modal-detalhe-info__body">
                <div className="modal-detalhe-info__section">
                    {/* Identificação */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        Identificação
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderField("Escola", apresentacao.escola, 24)}
                        {renderField("Aluno(a)", apresentacao.aluno, 12)}
                        {renderField("Idade", apresentacao.idade, 12)}
                        {renderField("Data de nascimento", apresentacao.data_nascimento, 12)}
                        {renderField("Série", apresentacao.serie, 12)}
                        {renderField("Período", apresentacao.periodo, 12)}
                        {renderField("Responsável", apresentacao.responsavel, 12)}
                        {renderField("Professor(a) AEE", apresentacao.professor_aee, 12)}
                        {renderField("Professor(a) regular", apresentacao.professor_regular, 12)}
                        {renderField("Laudo Médico", apresentacao.laudo_medico, 12)}
                        {renderField("CID", apresentacao.cid, 12)}

                        <Col span={12} style={{ marginBottom: 12 }}>
                            <span className="field-label">Deficiências</span>
                            <Skeleton loading={isLoading} paragraph={false} active className="field-skeleton">
                                {apresentacao.deficiencias?.length ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                                        {apresentacao.deficiencias.map(d => (
                                            <span key={d} style={{ fontSize: 12, fontWeight: "bold" }}>
                                                • {d}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="field-value" style={{ fontWeight: "bold", marginBottom: 0 }}>
                                        -
                                    </p>
                                )}
                            </Skeleton>
                        </Col>

                        <Col span={12} style={{ marginBottom: 12 }}>
                            <span className="field-label">Transtornos</span>
                            <Skeleton loading={isLoading} paragraph={false} active className="field-skeleton">
                                {apresentacao.transtornos?.length ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                                        {apresentacao.transtornos.map(t => (
                                            <span key={t} style={{ fontSize: 12, fontWeight: "bold" }}>
                                                • {t}
                                            </span>
                                        ))}
                                        {apresentacao.transtorno_outro && (
                                            <span style={{ fontSize: 12, color: "#555" }}>({apresentacao.transtorno_outro})</span>
                                        )}
                                    </div>
                                ) : (
                                    <p className="field-value" style={{ fontWeight: "bold", marginBottom: 0 }}>
                                        -
                                    </p>
                                )}
                            </Skeleton>
                        </Col>

                        {renderField("Medicação", apresentacao.medicacao, 12)}
                        {renderField("Horários", apresentacao.horarios_medicacao, 12)}
                        {renderFieldWide("Atendimento Educacional Especializado (dias e horários)", apresentacao.atendimento_aee)}
                    </Row>

                    {/* Atendimentos Clínicos */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        Atendimentos Clínicos
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {TERAPIAS.map(terapia => {
                            const key = terapia.toLowerCase().replace(/ /g, "_");
                            const dados = atendimentos[key] || {};
                            const temDados = dados.dias || dados.horario || dados.local;
                            if (!temDados && !isLoading) return null;
                            return (
                                <Col span={24} key={key} style={{ marginBottom: 10 }}>
                                    <span className="field-label">{terapia}</span>
                                    <Row gutter={16} style={{ marginTop: 4 }}>
                                        {renderField("Dias", dados.dias, 8)}
                                        {renderField("Horário", dados.horario, 8)}
                                        {renderField("Local", dados.local, 8)}
                                    </Row>
                                </Col>
                            );
                        })}
                    </Row>

                    {/* 1. Características do Aluno */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        1. Características do Aluno
                    </Divider>
                    <Row
                        gutter={8}
                        style={{ fontWeight: "bold", fontSize: 12, marginBottom: 6, padding: "4px 0", borderBottom: "1px solid #e8e8e8" }}
                    >
                        <Col span={6}>Área</Col>
                        <Col span={6}>Habilidades</Col>
                        <Col span={6}>Dificuldades</Col>
                        <Col span={6}>Artifícios e recursos</Col>
                    </Row>
                    {AREAS_CARACTERISTICAS.map(area => {
                        const dados = caracteristicas[area.key] || {};
                        return (
                            <Row key={area.key} gutter={8} style={{ padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12 }}>
                                <Col span={6}>
                                    <Skeleton loading={isLoading} paragraph={false} active>
                                        <span>{area.label}</span>
                                    </Skeleton>
                                </Col>
                                {["habilidades", "dificuldades", "artificios"].map(campo => (
                                    <Col span={6} key={campo}>
                                        <Skeleton loading={isLoading} paragraph={false} active>
                                            <p className="field-value--wide" style={{ fontSize: 12, fontWeight: "bold", marginBottom: 0 }}>
                                                {dados[campo] || "-"}
                                            </p>
                                        </Skeleton>
                                    </Col>
                                ))}
                            </Row>
                        );
                    })}

                    {/* 2. Intervenções */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        2. Tipo de Intervenções Necessárias
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {TIPOS_INTERVENCAO.map(item => renderFieldWide(item.label, intervencoes[item.key]))}
                    </Row>

                    {/* 3. Registro de Avanços */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        3. Registro dos Avanços e Dificuldades
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderFieldWide(null, plano?.registro_avancos)}
                    </Row>

                    {/* 4. Avaliação */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        4. Avaliação
                    </Divider>
                    <div style={{ marginBottom: 8, padding: "6px 8px", background: "#f5f5f5", borderRadius: 4, fontSize: 11 }}>
                        <strong>Legenda:</strong>&nbsp;
                        <BadgeAvaliacao valor="ALC" /> Alcançado &nbsp;|&nbsp;
                        <BadgeAvaliacao valor="EPR" /> Em Processo &nbsp;|&nbsp;
                        <BadgeAvaliacao valor="NAL" /> Não Alcançado &nbsp;|&nbsp;
                        <BadgeAvaliacao valor="NAV" /> Não Avaliado
                    </div>
                    {ITENS_AVALIACAO.map((item, index) => {
                        if (item.secao) {
                            return (
                                <div
                                    key={`secao_${index}`}
                                    style={{
                                        background: "#d9ead3",
                                        padding: "5px 10px",
                                        fontWeight: "bold",
                                        fontSize: 11,
                                        marginTop: 10,
                                        marginBottom: 2,
                                        borderRadius: 3
                                    }}
                                >
                                    {item.secao}
                                </div>
                            );
                        }
                        return (
                            <div key={item.key} style={{ padding: "4px 8px", borderBottom: "1px solid #f0f0f0" }}>
                                <Row align="middle">
                                    <Col span={18}>
                                        <Skeleton loading={isLoading} paragraph={false} active>
                                            <span style={{ fontSize: 12 }}>{item.label}</span>
                                        </Skeleton>
                                    </Col>
                                    <Col span={6} style={{ textAlign: "right" }}>
                                        <Skeleton loading={isLoading} paragraph={false} active>
                                            <BadgeAvaliacao valor={avaliacao[item.key]} />
                                        </Skeleton>
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}

                    {/* Observações Complementares */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        Observações Complementares
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderFieldWide(null, plano?.observacoes_complementares)}
                    </Row>
                </div>
            </div>
        </Modal>
    );
};

export default ModalPlanoAtendimentoPEI;
